<template>
  <div class="w-full flex flex-col items-start justify-start gap-6 px-4 lg:px-48 pb-5 lg:pb-10">
    <!-- Single white box containing all registrants -->
    <div class="bg-white rounded-xl shadow-md flex flex-col items-start text-gray-800 w-full">
      <p class="px-6 py-2 font-medium text-base text-white lg:text-lg bg-primary w-full mt-5 lg:mt-10">
        Registration Form
      </p>

      <div class="p-3 lg:p-6 w-full">
        <!-- All registrants -->
        <div v-for="(registrant, index) in registrants" :key="registrant.id"
          class="mb-6 last:border-none last:pb-0 last:mb-0">
          <!-- Header -->
          <div
            class="flex flex-col mb-3 text-sm lg:text-base cursor-pointer select-none bg-gray-100 p-2 lg:p-4 rounded-md">
            <div class="flex justify-between items-center" @click="toggleRegistrant(index)">
              <div class="flex items-center space-x-2">
                <p class="font-semibold text-primary">
                  Registrant #{{ index + 1 }}
                </p>
                <img :class="[
                  'w-4 h-4 transform transition-transform duration-300',
                  registrant.collapsed ? 'rotate-0' : 'rotate-180'
                ]" src='@/assets/icons/collapse_icon.svg' alt="toggle" />
              </div>

              <button v-if="index > 0 || (index === 0 && registrants.length > 1)" @click.stop="removeRegistrant(index)"
                class="text-error font-medium flex flex-row items-center justify-center hover:underline text-xs lg:text-base">
                <span class="material-icons text-error mr-1">
                  <img src="@/assets/icons/delete_icon.svg" class="w-4 lg:w-5 mt-1" />
                </span>
                Remove
              </button>

            </div>

            <!-- Show full name only when collapsed -->
            <p v-if="registrant.collapsed && (registrant.fname || registrant.lname)"
              class="text-gray-500 text-sm lg:text-base mt-1">
              {{ registrant.fname || '' }} {{ registrant.lname || '' }} |
              {{
                optionsCategory.find(option => option.value === registrant.category)?.label || registrant.category
              }}
              <span v-if="registrant.distance">({{ getDistanceLabel(registrant) }})</span>
            </p>
          </div>


          <!-- Collapsible Content -->
          <transition name="fade-slide">
            <div v-show="!registrant.collapsed" class="form-grid w-full mt-5 lg:mt-5">
              <TextControl label="First Name" fieldId="fname" required v-model="registrant.fname" :max-length="64"
                :enableValidation="true" />
              <TextControl label="Middle Name" fieldId="mname" v-model="registrant.mname" :max-length="64"
                :enableValidation="true" />
              <TextControl label="Last Name" fieldId="lname" required v-model="registrant.lname" :max-length="64"
                :enableValidation="true" />
              <TextControl label="Email Address" fieldId="email" required v-model="registrant.email" :max-length="64"
                :enableValidation="true" />
              <TextControl label="Mobile Number" fieldId="mobile" required v-model="registrant.mobile"
                :enableValidation="true" />

              <div class="form-grid">
                <TextControl label="Age" fieldId="age" required v-model="registrant.age" :max-length="2"
                  @update:modelValue="validateAge(registrant)" :enable-validation="true" />
                <ComboBoxControl label="Gender" fieldId="gender" required :options="optionsGender"
                  v-model="registrant.gender" />
              </div>

              <div class="form-grid">
                <ComboBoxControl label="Segment" fieldId="segment" required :options="getSegmentOptions(registrant)"
                  v-model="registrant.segment" @change="handleSegmentChange(registrant)" />
                <ComboBoxControl v-if="!isSponsorTypeDisabled(registrant)" label="Sponsor Type" fieldId="sponsor_type"
                  :required="isSponsorTypeRequired(registrant)" :disabled="isSponsorTypeDisabled(registrant)"
                  :options="optionsSponsorType" v-model="registrant.sponsor_type" />
                <ComboBoxControl v-else :label="registrant.segment === 'EMP' ? 'Office' : 'Branch Service'"
                  fieldId="branch_service" :required="isBranchRequired(registrant)"
                  :disabled="isBranchDisabled(registrant)" :options="getBranchOptions(registrant.segment)"
                  v-model="registrant.branch_service" />
              </div>

              <div class="form-grid">
                <TextControl label="Account Number" fieldId="account_no"
                  :required="isAccountRequired(registrant) && !registrant.serial_no"
                  :disabled="isAccountDisabled(registrant) || !!registrant.serial_no" v-model="registrant.account_no"
                  :max-length="6" />
                <TextControl label="Serial Number" fieldId="serial_no"
                  :required="isAccountRequired(registrant) && !registrant.account_no"
                  :disabled="isAccountDisabled(registrant) || !!registrant.account_no" v-model="registrant.serial_no"
                  :max-length="7" />
              </div>

              <ComboBoxControl label="Select Event Category" required fieldId="category" :options="optionsCategory"
                v-model="registrant.category" @change="handleCategoryChange(registrant)" />

              <ComboBoxControl label="Choose Race Distance" :required="!isDistanceDisabled(registrant)"
                fieldId="distance" :options="getDistanceOptions(registrant.segment)" v-model="registrant.distance"
                :disabled="isDistanceDisabled(registrant)" />



              <div
                class="form-grid col-span-full-lg w-full p-4 rounded-md border flex justify-between items-center shadow-sm transition select-none"
                :class="[
                  registrant.waiverSubmitted
                    ? 'bg-primary-light border-primary text-primary hover:bg-green-100 cursor-pointer'
                    : !registrant.age
                      ? 'bg-gray-50 border-gray-600 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-50 hover:bg-gray-100 border-gray-600 text-gray-500 cursor-pointer'
                ]" @click="registrant.age ? openWaiver(index) : null">
                <p class="text-sm lg:text-base font-medium">
                  {{ registrant.age
                    ? (registrant.waiverSubmitted
                      ? 'Waiver Submitted (Click to View/Edit)'
                      : 'Click here to review and sign the Event Waiver and Release of Liability')
                    : 'Please enter your age to continue with the waiver.' }}
                  <span v-if="!registrant.waiverSubmitted && registrant.age" class="text-error">*</span>
                </p>
              </div>



              <!-- Waiver Section -->


            </div>
          </transition>
        </div>



        <div v-if="registrants.length < 3" @click="addRegistrant" :class="[
          'text-sm lg:text-base mt-5 w-full rounded-md border-2 border-dashed border-primary items-center justify-center flex bg-white hover:bg-gray-100 cursor-pointer p-3 font-medium space-x-2',
          !isLastRegistrantComplete ? 'opacity-50 cursor-not-allowed hover:bg-white' : 'text-primary'
        ]" :disabled="!isLastRegistrantComplete">
          <img src="@/assets/icons/add_icon.svg" alt="add" class="w-5 h-5" />
          <p>Add Another Registrant</p>
        </div>

        <!-- Hint when max reached -->
        <p v-else class="text-sm text-gray-500 mt-3 text-center w-full italic">
          You can only register up to 3 participants per batch.
        </p>

        <!-- Single Button Section -->
        <div class="w-full flex justify-end mt-10">
          <div class="w-full flex flex-row lg:w-1/3 space-x-3">
            <ButtonControl variant="secondary" @click="handleBack()"> BACK </ButtonControl>
            <ButtonControl variant="primary" @click="saveAndNext" :disabled="!isFormComplete || !waiverSubmitted"> NEXT
            </ButtonControl>
          </div>
        </div>
      </div>
    </div>


    <!-- Data Privacy Modal -->
    <transition name="slide-in">
      <ModalControl v-if="data_privacy_modal" :is_visible="data_privacy_modal" :width="'w-5/6 lg:w-1/2'"
        @close="this.$router.push('/')">
        <DataPrivacyContent @close="data_privacy_modal = false" />
      </ModalControl>
    </transition>

    <!-- Waiver Modal -->
    <transition name="slide-in">
      <ModalControl v-if="show_waiver" :is_visible="show_waiver" :width="'w-5/6 lg:w-1/2'" @close="show_waiver = false">
        <Waiver v-if="activeWaiverIndex !== null" :registrant="registrants[activeWaiverIndex]"
          :registrants="registrants"
          :name="registrants[activeWaiverIndex].fname + ' ' + registrants[activeWaiverIndex].lname"
          :isMinor="registrants[activeWaiverIndex].isMinor" :savedData="registrants[activeWaiverIndex].waiverData"
          @submitted="handleWaiverSubmitted" :guardianOptions="guardianOptions"
          :age="registrants[activeWaiverIndex].age" />
      </ModalControl>
    </transition>

    <transition name="slide-in">
      <MessageBoxControl v-if="message_modal" :is_visible="message_modal" :positiveButton="btn_text"
        @positiveClick="handlePositiveClick" :theme="theme" :title="title" :message="message" />
    </transition>

  </div>
</template>

<script>
import ButtonControl from "@/components/controls/ButtonControl.vue";
import TextControl from "@/components/controls/TextControl.vue";
import ComboBoxControl from "@/components/controls/ComboBoxControl.vue";
import ModalControl from "@/components/controls/ModalControl.vue";
import DataPrivacyContent from "@/components/contents/DataPrivacyContent.vue";
import MessageBoxControl from "@/components/controls/MessageBoxControl.vue";
import Waiver from "@/components/contents/Waiver.vue";
import user from "@/services/user";
import { nextTick } from "vue";
import confirmLeave from "@/mixins/confirmLeave";

export default {
  name: "RegistrationView",
  inject: ["setLoading"],
  mixins: [confirmLeave],
  components: {
    ButtonControl,
    TextControl,
    ComboBoxControl,
    ModalControl,
    DataPrivacyContent,
    MessageBoxControl,
    Waiver
  },
  computed: {
    isLastRegistrantComplete() {
      const last = this.registrants[this.registrants.length - 1];
      if (!last) return false;

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const mobileRegex = /^(09|\+639)\d{9}$/;

      const hasAllRequiredValues =
        last.fname &&
        last.lname &&
        last.email &&
        last.mobile &&
        last.age >= 12 &&
        last.gender &&
        last.segment &&
        (this.isBranchDisabled(last) || last.branch_service) &&
        (this.isAccountRequired(last) ? (last.account_no || last.serial_no) : true) &&
        last.category &&
        (last.category === "1" || last.category === "3" ? last.distance : true);

      const isEmailValid = emailRegex.test(last.email);
      const isMobileValid = mobileRegex.test(last.mobile);

      return hasAllRequiredValues && isEmailValid && isMobileValid;
    },

    isFirstRegistrantComplete() {
      if (!this.registrants[0]) return false;
      const r = this.registrants[0];
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const mobileRegex = /^(09|\+639)\d{9}$/;

      const hasAllRequiredValues =
        r.fname &&
        r.lname &&
        r.email &&
        r.mobile &&
        r.age &&
        r.gender &&
        r.segment &&
        (this.isBranchDisabled(r) || r.branch_service) &&
        (this.isAccountRequired(r) ? (r.account_no || r.serial_no) : true) &&
        r.category &&
        (r.category === "1" || r.category === "3" ? r.distance : true);

      const isEmailValid = emailRegex.test(r.email);
      const isMobileValid = mobileRegex.test(r.mobile);

      // ✅ Include waiver submission in the completeness check
      return hasAllRequiredValues && isEmailValid && isMobileValid && r.waiverSubmitted;
    },
    isFormComplete() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const mobileRegex = /^(09|\+639)\d{9}$/;

      return this.registrants.every((r) => {
        const hasAllRequiredValues =
          r.fname &&
          r.lname &&
          r.email &&
          r.mobile &&
          r.age >= 12 &&
          r.gender &&
          r.segment &&
          (this.isBranchDisabled(r) || r.branch_service) &&
          (this.isAccountRequired(r) ? (r.account_no || r.serial_no) : true)
        r.category &&
          // ✅ Distance is only required if category is Fun Run (1) or Both (3)
          (r.category === "1" || r.category === "3" ? r.distance : true);

        const isEmailValid = emailRegex.test(r.email);
        const isMobileValid = mobileRegex.test(r.mobile);

        return hasAllRequiredValues && isEmailValid && isMobileValid;
      });
    },

    // 🟢 NEW computed to check waiver completion for all registrants
    waiverSubmitted() {
      return this.registrants.every(r => r.waiverSubmitted);
    },
    optionsSegment() {
      return this.$store.state.segment || [];
    },
  },
  watch: {
  registrants: {
    handler(newVal) {
      newVal.forEach((r) => {
        // ✅ Convert age to number only if filled
        if (r.age !== null && r.age !== undefined && r.age !== '') {
          r.age = Number(r.age);
          r.isMinor = r.age <= 17;
        } else {
          r.isMinor = false;
        }

        // ✅ Ensure mutual exclusivity for account_no and serial_no
        if (r.account_no && r.serial_no) {
          r.serial_no = null;
        }
        if (r.serial_no && r.account_no) {
          r.account_no = null;
        }
      });

      // 🧭 Update guardian options (list of all names)
      this.guardianOptions = newVal
        .filter(r => r.fname && r.lname)
        .map(r => `${r.fname} ${r.lname}`);
    },
    deep: true,
    immediate: true,
  },
},


  data() {
    return {
      message_modal: false,
      theme: null,
      title: null,
      message: null,
      btn_text: null,
      guardianOptions: [],
      collapsed: false,
      uuid: null,
      show_waiver: false,
      showExitDialog: false,
      data_privacy_modal: false,
      activeWaiverIndex: null,
      registrants: [
        {
          id: Date.now(),  // unique id
          fname: null,
          mname: null,
          lname: null,
          email: null,
          mobile: null,
          age: null,
          gender: null,
          segment: null,
          sponsor_type: null,
          branch_service: null,
          account_no: null,
          serial_no: null,
          category: null,
          distance: null,
          waiverSubmitted: false,
          waiverData: {},
          isMinor: false,
          collapsed: false,
        }
      ],
      optionsGender: [
        { value: "M", label: "MALE" },
        { value: "F", label: "FEMALE" },
        { value: "X", label: "RATHER NOT SAY" },
      ],
      optionsCategory: [
        { value: "1", label: "FUN RUN" },
        { value: "2", label: "ZUMBA" },
        { value: "3", label: "BOTH FUN RUN AND ZUMBA" },
      ],
      optionsSponsorType: [
        { value: "P", label: "PLATINUM" },
        { value: "G", label: "GOLD" },
        { value: "S", label: "SILVER" },
        { value: "B", label: "BRONZE" },
      ],
      branchOptions: {
        MEM: [
          { value: "PA", label: "PHILIPPINE ARMY" },
          { value: "PN", label: "PHILIPPINE NAVY" },
          { value: "PAF", label: "PHILIPPINE AIR FORCE" },
          { value: "RET", label: "RETIREE" },
          { value: "DEP", label: "DEPENDENT" },
          { value: "CE", label: "CIVILIAN EMPLOYEE" },
        ],
        EMP: [
          { value: "HO", label: "MAIN OFFICE" },
          { value: "PSO", label: "SATELLITE OFFICE" },
        ],
      },
    };
  },
  mounted() {
    if (!this.$store.state.segment) {
      this.getSegments();
    }

    // Restore previously saved form if it exists
    if (this.$store.state.form && this.$store.state.form.length > 0) {
      this.registrants = JSON.parse(JSON.stringify(this.$store.state.form));
    }

    const params = new URLSearchParams(window.location.search);
    const uuidParam = params.get("uuid") || null;

    if (uuidParam) {
      this.uuid = uuidParam;

      // ✅ Check if user has already registered
      user.hasRegistered(this.uuid)
        .then((response) => {
          const hasRegistered = response.data.data.has_registered;

          nextTick(() => {
            if (!hasRegistered) {
              // Autofill fields ONLY if not registered yet
              if (params.has("fname")) this.registrants[0].fname = params.get("fname");
              if (params.has("mname")) this.registrants[0].mname = params.get("mname");
              if (params.has("lname")) this.registrants[0].lname = params.get("lname");
              if (params.has("email")) this.registrants[0].email = params.get("email");

              if (params.has("mobile")) {
                let mobile = params.get("mobile") ?? "";   // nullish coalescing
                mobile = String(mobile).trim();            // ensure string
                if (mobile.startsWith("+63")) mobile = mobile.slice(3);
                if (mobile.startsWith("0")) mobile = mobile.slice(1);
                mobile = mobile.replace(/\s+/g, "");

                this.registrants[0].mobile = mobile ? "+63" + mobile : null;
              }

              if (params.has("age")) this.registrants[0].age = params.get("age");

              if (params.has("gender")) {
                const genderParam = params.get("gender");
                const genderMap = { Male: "M", Female: "F", "Rather not say": "X" };
                this.registrants[0].gender = genderMap[genderParam] || null;
              }

              // Autofill account_no ONLY if membership_type is REGULAR PAFCPIC or missing
              if (params.has("account_no")) {
                this.registrants[0].account_no = params.get("account_no");
              }
            } else {
              // ✅ Already registered: do NOT autofill
              console.log("User already registered. Skipping autofill.");
            }
          });
        })
        .catch(() => {
          // Fallback: just autofill if endpoint fails
          nextTick(() => {
            console.warn("Could not verify registration. Proceeding with autofill.");
            if (params.has("fname")) this.registrants[0].fname = params.get("fname");
            if (params.has("mname")) this.registrants[0].mname = params.get("mname");
            if (params.has("lname")) this.registrants[0].lname = params.get("lname");
            if (params.has("email")) this.registrants[0].email = params.get("email");

            if (params.has("mobile")) {
              let mobile = params.get("mobile") ?? "";   // nullish coalescing
              mobile = String(mobile).trim();            // ensure string
              if (mobile.startsWith("+63")) mobile = mobile.slice(3);
              if (mobile.startsWith("0")) mobile = mobile.slice(1);
              mobile = mobile.replace(/\s+/g, "");

              this.registrants[0].mobile = mobile ? "+63" + mobile : null;
            }

            if (params.has("age")) this.registrants[0].age = params.get("age");

            if (params.has("gender")) {
              const genderParam = params.get("gender");
              const genderMap = { Male: "M", Female: "F", "Rather not say": "X" };
              this.registrants[0].gender = genderMap[genderParam] || null;
            }
          });
        })
        .finally(() => {
          // Always replace the URL to clean query parameters
          this.$router.replace({ path: this.$route.path });
        });
    } else {
      // No UUID: just autofill from params as usual
      nextTick(() => {
        if (params.has("fname")) this.registrants[0].fname = params.get("fname");
        if (params.has("mname")) this.registrants[0].mname = params.get("mname");
        if (params.has("lname")) this.registrants[0].lname = params.get("lname");
        if (params.has("email")) this.registrants[0].email = params.get("email");

        if (params.has("mobile")) {
          let mobile = params.get("mobile") ?? "";   // nullish coalescing
          mobile = String(mobile).trim();            // ensure string
          if (mobile.startsWith("+63")) mobile = mobile.slice(3);
          if (mobile.startsWith("0")) mobile = mobile.slice(1);
          mobile = mobile.replace(/\s+/g, "");

          this.registrants[0].mobile = mobile ? "+63" + mobile : null;
        }

        if (params.has("age")) this.registrants[0].age = params.get("age");

        if (params.has("gender")) {
          const genderParam = params.get("gender");
          const genderMap = { Male: "M", Female: "F", "Rather not say": "X" };
          this.registrants[0].gender = genderMap[genderParam] || null;
        }

        this.$router.replace({ path: this.$route.path });
      });
    }
  },

  methods: {
    handlePositiveClick() {
      if (this.theme === "success" || this.theme === "info") {
        this.$router.push("/");
      } else {
        this.message_modal = false;
      }
    },
    getSegmentOptions(registrant) {
      if (registrant.age !== null && registrant.age <= 17) {
        // Only allow "NDP" for minors
        return this.optionsSegment.filter(s => s.value === "NON");
      }
      // For adults, show all options
      return this.optionsSegment;
    },
    getDistanceLabel(registrant) {
      if (!registrant.segment || !registrant.distance || !this.$store.state.distance) return "";

      const distanceObj = this.$store.state.distance.find(d => d[registrant.segment]);
      if (!distanceObj) return "";

      const distanceOption = distanceObj[registrant.segment].find(d => d.value === registrant.distance);
      return distanceOption ? distanceOption.label : registrant.distance;
    },
    openWaiver(index) {
      this.activeWaiverIndex = index;
      this.show_waiver = true;
    },
    validateAge(registrant) {
      if (registrant.age !== null && registrant.age !== undefined && registrant.age !== '') {
        registrant.age = Number(registrant.age);
        registrant.isMinor = registrant.age <= 17;
        console.log('Minor:', registrant.isMinor, 'Age:', registrant.age);
      } else {
        registrant.isMinor = false;
      }
    },
    handleWaiverSubmitted(waiverData) {
      if (this.activeWaiverIndex !== null) {
        const registrant = this.registrants[this.activeWaiverIndex];
        registrant.waiverSubmitted = true;
        registrant.waiverData = { ...waiverData }; // 🟢 Save all waiver responses persistently
        this.$store.state.form = JSON.parse(JSON.stringify(this.registrants));
      }
      this.show_waiver = false;
      this.activeWaiverIndex = null;
    },

    getDistanceOptions(segment) {
      if (!segment || !this.$store.state.distance) return [];

      // Find the matching distance object for the selected segment
      const distanceObj = this.$store.state.distance.find(d => d[segment]);
      if (!distanceObj) return [];

      // Map the distance list into label/value/disabled format
      return distanceObj[segment].map(item => ({
        value: item.value,
        label: item.slot ? item.label : `${item.label} - NO SLOT AVAILABLE`,
        disabled: !item.slot
      }));
    },
    handleBack() {
      if (this.$store.state.fromConfirm) {
        this.$router.back();
      } else {
        const confirmExit = window.confirm(
          "Are you sure you want to exit? This will return you to the homepage."
        );
        if (confirmExit) {
          this.$router.push("/");
        }
      }
    },
    isDistanceDisabled(registrant) {
      return registrant.category === "2"
      ;
    },
    handleCategoryChange(registrant) {
      // If category is Zumba, clear distance and disable it
      if (registrant.category === "2") {
        registrant.distance = null;
      }
    },
    getSegments() {
      this.setLoading(true);
      user.getSegment()
        .then((response) => {
          const data = response.data.data;
          this.$store.state.segment = data.segment;
          this.$store.state.distance = data.distance;
          this.$store.state.date = response.data.timestamp;
          this.data_privacy_modal = true;
          this.setLoading(false);
        })
        .catch(() => {
          this.setLoading(false);
        });
    },
    toggleRegistrant(index) {
      this.registrants[index].collapsed = !this.registrants[index].collapsed;
    },
    addRegistrant() {
      if (this.registrants.length >= 3) return;
      if (!this.isFirstRegistrantComplete || !this.isLastRegistrantComplete) return;

      this.registrants.forEach(r => (r.collapsed = true));

      this.registrants.push({
        id: Date.now() + Math.random(),  // unique!
        fname: null,
        mname: null,
        lname: null,
        email: null,
        mobile: null,
        age: null,
        gender: null,
        segment: null,
        sponsor_type: null,
        branch_service: null,
        account_no: null,
        serial_no: null,
        category: null,
        distance: null,
        waiverSubmitted: false,
        collapsed: false,
      });
    },

    removeRegistrant(index) {
      // Prevent removing a registrant who is referenced as a guardian by any minor
      const toRemove = this.registrants[index];
      const fullName = `${toRemove.fname || ""} ${toRemove.lname || ""}`.trim();

      const isReferencedAsGuardian = this.registrants.some((r, i) => {
        if (i === index) return false;
        // check saved waiverData.guardian_name (saved guardian from submitted waiver)
        if (r.waiverData && r.waiverData.guardian_name) {
          if (String(r.waiverData.guardian_name).trim().toLowerCase() === fullName.toLowerCase()) {
            return true;
          }
        }
        // defensive check: some implementations might store guardian_name at top-level
        if (r.guardian_name && String(r.guardian_name).trim().toLowerCase() === fullName.toLowerCase()) {
          return true;
        }
        return false;
      });

      if (isReferencedAsGuardian) {
        this.theme = "error";
        this.title = "Cannot Remove Participant";
        this.message =
          "This participant is listed as the guardian for a minor in the form. Please change or remove the minor's guardian from the waiver before removing this participant.";
        this.btn_text = "OK";
        this.message_modal = true;
        return;
      }

      this.registrants.splice(index, 1);

      // Reset active waiver if it was the removed registrant
      if (this.activeWaiverIndex !== null) {
        if (this.activeWaiverIndex === index) {
          this.activeWaiverIndex = null;
          this.show_waiver = false;
        } else if (this.activeWaiverIndex > index) {
          // Shift index down if after removed registrant
          this.activeWaiverIndex--;
        }
      }
    },

    handleSegmentChange(registrant) {
      registrant.sponsor_type = null;
      registrant.branch_service = null;
      registrant.distance = null;

      // Only clear account_no if segment is NOT EMP, MEM, DEP
      if (!["EMP", "MEM", "DEP"].includes(registrant.segment)) {
        registrant.account_no = null;
      }
    },
    getBranchOptions(segment) {
      return this.branchOptions[segment] || [];
    },
    isSponsorTypeDisabled(registrant) {
      return registrant.segment !== "SPR";
    },
    isSponsorTypeRequired(registrant) {
      return registrant.segment === "SPR";
    },
    isBranchDisabled(registrant) {
      return !this.branchOptions[registrant.segment];
    },
    isBranchRequired(registrant) {
      return !this.isBranchDisabled(registrant);
    },
    isAccountRequired(registrant) {
      return ["MEM", "EMP", "DEP"].includes(registrant.segment);
    },
    isAccountDisabled(registrant) {
      return registrant.segment === "SPR" || !this.isAccountRequired(registrant);
    },
    saveAndNext() {
      this.$store.state.form = this.registrants;
      this.$store.state.uuid = this.uuid;
      console.log("Saved Form Data:", this.$store.state.form);
      console.log("Saved UUID:", this.$store.state.uuid);

      if (this.$store.state.fromConfirm) {
        this.$store.state.fromConfirm = false;
        this.$router.push("/confirm");
      } else {
        this.$router.push("/reminders");
      }
    },
  },
};
</script>

<style scoped>
.material-icons {
  font-size: 18px;
  vertical-align: middle;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 1.25rem;
  /* gap-5 */
}

@media (min-width: 1024px) {
  .form-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.col-span-full-lg {
  grid-column: span 1 / span 1;
  /* default for small screens */
}

@media (min-width: 1024px) {
  .col-span-full-lg {
    grid-column: span 2 / span 2;
    /* span 2 columns on lg+ screens */
  }
}
</style>